"use client"

import { ArrowRight, CircleCheck } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import type { MetaAdsFormHandoff } from "@/lib/meta-ads/types"

export function MetaAdsLiveAdHandoff({
  handoffs,
  adAccountName,
}: {
  handoffs: MetaAdsFormHandoff[]
  adAccountName: string | null
}) {
  const allAcknowledged = handoffs.every(handoff => handoff.acknowledgedAt)

  return (
    <Alert>
      <AlertTitle>
        {allAcknowledged
          ? "Live Ad Updates Confirmed"
          : "Step 2: Select the New Form in Ads Manager"}
      </AlertTitle>
      <AlertDescription className="space-y-4">
        <p>
          Update each ad that uses a current form shown below. Meta does not switch
          forms on live ads automatically.
        </p>

        <div className="space-y-3" aria-label="Instant Form replacements">
          {handoffs.map((handoff, index) => (
            <FormReplacement key={handoff.id} handoff={handoff} index={index} />
          ))}
        </div>

        {!allAcknowledged ? (
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <span>Open Ads Manager</span>
              {adAccountName ? <> in <strong>{adAccountName}</strong></> : ""}<span>. It opens
              the</span> <strong> Ads</strong> <span>tab.</span>
            </li>
            <li>
              <span>For each replacement above, find every ad using the current form,
              choose</span> <strong>Edit</strong><span>, and select the new form under</span>
              <strong> Instant Form</strong><span>.</span>
            </li>
            <li>
              <span>If a campaign is completed, open the</span> <strong>Campaigns</strong> <span>tab and
              duplicate it. Set a new schedule in the duplicated</span> <strong>Ad set</strong><span>,
              then select the new form on the duplicated</span> <strong>Ad</strong><span>.</span>
            </li>
            <li><span>Select</span> <strong>Publish</strong><span>, then return here.</span></li>
          </ol>
        ) : null}
      </AlertDescription>
    </Alert>
  )
}

function FormReplacement({
  handoff,
  index,
}: {
  handoff: MetaAdsFormHandoff
  index: number
}) {
  return (
    <section
      className="rounded-md border bg-background p-3"
      aria-label={`Form replacement ${index + 1}`}
    >
      <div className="grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <FormIdentity label="Current form in the ad" form={handoff.sourceForm} />
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground sm:flex-col sm:gap-1">
          <span>Replace with</span>
          <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </div>
        <FormIdentity label="New Versafy form" form={handoff.copiedForm} />
      </div>
      {handoff.acknowledgedAt ? (
        <Badge className="mt-3 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
          <CircleCheck aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
          <span>Update confirmed</span>
        </Badge>
      ) : null}
    </section>
  )
}

function FormIdentity({
  label,
  form,
}: {
  label: string
  form: { id: string; name: string }
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="break-words text-sm font-medium text-foreground">{form.name}</p>
      <p className="text-xs text-muted-foreground"><span>Form ID</span> {form.id}</p>
    </div>
  )
}
